import Keychain from 'react-native-keychain';
import {
  KEYCHAIN_SERVICE_BIOMETRIC,
  KEYCHAIN_SERVICE_LEGACY,
  KEYCHAIN_SERVICE_PLAIN,
} from '../constants/StorageKeys';

export type BiometryType = Awaited<
  ReturnType<typeof Keychain.getSupportedBiometryType>
>;

export interface StoredCredentials {
  username: string;
  password: string;
}

/**
 * Retorna o tipo de biometria suportado pelo aparelho (FaceID, TouchID, Fingerprint...)
 * ou null quando não há biometria disponível/cadastrada.
 */
export const getSupportedBiometry = async (): Promise<BiometryType> => {
  try {
    return await Keychain.getSupportedBiometryType();
  } catch {
    return null;
  }
};

/**
 * Label amigável para exibir na UI conforme o tipo de biometria do aparelho.
 */
export const getBiometryLabel = (type: BiometryType): string => {
  if (type === Keychain.BIOMETRY_TYPE.FACE_ID || type === Keychain.BIOMETRY_TYPE.FACE) {
    return 'Face ID';
  }
  if (
    type === Keychain.BIOMETRY_TYPE.TOUCH_ID ||
    type === Keychain.BIOMETRY_TYPE.FINGERPRINT
  ) {
    return 'Digital';
  }
  return 'Biometria';
};

/**
 * Salva as credenciais protegidas por biometria. A leitura futura exigirá
 * autenticação (Face ID / Digital) do usuário.
 */
export const saveCredentialsBiometric = async (
  username: string,
  password: string,
): Promise<void> => {
  await Keychain.setGenericPassword(username, password, {
    service: KEYCHAIN_SERVICE_BIOMETRIC,
    accessControl: Keychain.ACCESS_CONTROL.BIOMETRY_CURRENT_SET,
    accessible: Keychain.ACCESSIBLE.WHEN_UNLOCKED_THIS_DEVICE_ONLY,
  });
  // A senha não deve continuar salva em texto quando a biometria está ativa.
  await clearPlainCredentials();
};

/**
 * Lê as credenciais protegidas por biometria, disparando o prompt de Face ID/Digital.
 * Retorna null se não houver credenciais salvas. Lança erro se o usuário cancelar
 * ou a biometria falhar.
 */
export const getCredentialsBiometric = async (
  promptTitle: string,
): Promise<StoredCredentials | null> => {
  const result = await Keychain.getGenericPassword({
    service: KEYCHAIN_SERVICE_BIOMETRIC,
    authenticationPrompt: {title: promptTitle},
  });
  if (!result) {
    return null;
  }
  return {username: result.username, password: result.password};
};

/**
 * Salva as credenciais sem proteção biométrica (comportamento do "Lembrar de mim").
 */
export const saveCredentialsPlain = async (
  username: string,
  password: string,
): Promise<void> => {
  await Keychain.setGenericPassword(username, password, {
    service: KEYCHAIN_SERVICE_PLAIN,
  });
};

/**
 * Lê as credenciais salvas sem prompt de biometria. Usado no prefill do "Lembrar de mim".
 * Não força autenticação; se a entrada estiver protegida por biometria, pode exigir prompt.
 */
export const getCredentialsPlain =
  async (): Promise<StoredCredentials | null> => {
    try {
      const result = await Keychain.getGenericPassword({
        service: KEYCHAIN_SERVICE_PLAIN,
      });
      if (!result) {
        return null;
      }
      return {username: result.username, password: result.password};
    } catch {
      return null;
    }
  };

/**
 * Remove apenas as credenciais em texto ("Lembrar de mim").
 */
export const clearPlainCredentials = async (): Promise<void> => {
  try {
    await Keychain.resetGenericPassword({service: KEYCHAIN_SERVICE_PLAIN});
  } catch {
    // silencioso: nada a limpar
  }
};

/**
 * Remove apenas as credenciais protegidas por biometria.
 */
export const clearBiometricCredentials = async (): Promise<void> => {
  try {
    await Keychain.resetGenericPassword({service: KEYCHAIN_SERVICE_BIOMETRIC});
  } catch {
    // silencioso: nada a limpar
  }
};

/**
 * Apaga a entrada antiga (anterior à separação de services). Nunca lemos essa
 * entrada porque ela pode estar protegida por biometria — apagar não exige
 * autenticação, ler exigiria.
 */
export const clearLegacyCredentials = async (): Promise<void> => {
  try {
    await Keychain.resetGenericPassword({service: KEYCHAIN_SERVICE_LEGACY});
  } catch {
    // silencioso: nada a limpar
  }
};

/**
 * Remove todas as credenciais salvas no keychain.
 */
export const clearCredentials = async (): Promise<void> => {
  await clearPlainCredentials();
  await clearBiometricCredentials();
  await clearLegacyCredentials();
};
